/* import __COLOCATED_TEMPLATE__ from './visibility-selector.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';

import type CustomIntlService from 'embercom/services/intl';
import { MacroVisibility } from 'embercom/objects/inbox/macro';
import { EntityType } from 'embercom/models/data/entity-types';
import type TeamSummary from 'embercom/objects/inbox/team-summary';
import type Session from 'embercom/services/session';

interface Args {
  visibility: MacroVisibility;
  selectedTeams: TeamSummary[];
  onSelectVisibility: (visibility: { id: MacroVisibility }) => void;
}

interface Signature {
  Args: Args;
}

export default class VisibilitySelector extends Component<Signature> {
  @service declare intl: CustomIntlService;
  @service declare session: Session;

  readonly teamEntity = EntityType.Team;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    // Default to personal macro if user can't create shared macros
    if (!this.canCreateSharedMacros && this.canUsePersonalMacros) {
      this.args.onSelectVisibility({ id: MacroVisibility.Myself });
    }
  }

  get canManageMacros() {
    return this.session.teammate.permissions.canManageMacros;
  }

  get canUsePersonalMacros() {
    return this.session.teammate.permissions.canUsePersonalMacros;
  }

  private get canCreateSharedMacros(): boolean {
    let permissions = this.session.teammate.permissions;
    if (!this.session.workspace.canUseFeature('spg-inbox-create-edit-macros')) {
      return permissions.canManageMacros;
    }
    return permissions.canManageMacros && permissions.canCreateMacros;
  }

  get visibility() {
    if (!this.canCreateSharedMacros && this.canUsePersonalMacros) {
      return MacroVisibility.Myself;
    } else {
      return this.args.visibility;
    }
  }

  get selectedTeamsLabel() {
    if (this.args.selectedTeams.length) {
      let selectedTeams = this.args.selectedTeams.map((team) =>
        team.icon ? `${team.icon.emoji} ${team.name}` : team.name,
      );
      return this.intl.formatList(selectedTeams, { style: 'long', type: 'conjunction' });
    } else {
      return this.intl.t('inbox.create-macro.team-selector.placeholder');
    }
  }

  get items() {
    let allItems = [MacroVisibility.Everyone, MacroVisibility.Teams, MacroVisibility.Myself];

    return allItems.map((visibility) => ({
      id: visibility,
      label: this.getVisibilityLabel(visibility),
      isSelected: this.visibility === visibility,
      isDisabled: this.shouldDisableOption(visibility),
      tooltipText: this.getDisabledTooltipText(visibility),
    }));
  }

  private getVisibilityLabel(visibility: MacroVisibility): string {
    switch (visibility) {
      case MacroVisibility.Everyone:
        return this.intl.t('inbox.create-macro.available-for.everyone');
      case MacroVisibility.Teams:
        return this.intl.t('inbox.create-macro.available-for.teams');
      case MacroVisibility.Myself:
        return this.intl.t('inbox.create-macro.available-for.owner');
      default:
        return '';
    }
  }

  get showTeamsDropdown() {
    return this.args.visibility === MacroVisibility.Teams;
  }

  private shouldDisableOption(visibility: MacroVisibility) {
    if (visibility === MacroVisibility.Myself) {
      return !this.canUsePersonalMacros;
    } else {
      return !this.canCreateSharedMacros;
    }
  }

  private getDisabledTooltipText(visibility: MacroVisibility): string | undefined {
    if (visibility === MacroVisibility.Myself && !this.canUsePersonalMacros) {
      return this.intl.t('inbox.create-macro.disabled-tooltip.personal');
    } else if (visibility !== MacroVisibility.Myself && !this.canCreateSharedMacros) {
      return this.intl.t('inbox.create-macro.disabled-tooltip.shared');
    }
    return undefined;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::CreateMacro::VisibilitySelector': typeof VisibilitySelector;
    'inbox2/create-macro/visibility-selector': typeof VisibilitySelector;
  }
}
